import { type FC, type JSX } from 'react'
import { FormattedMessage } from 'react-intl'

import { IconArrowRight, IconInfo } from '@redteclab/icons'

import { Dialog, DialogContent, DialogTitle } from 'base-ui'

import { useCdnMediaServer } from '../../../../../../cdn'
import { useOpenableControls } from '../../../../../../generic-hooks'
import { useGlobalConfigContext } from '../../../../../../global-config'
import { ProductHeadGenericLabel } from '../../../../../../product'

import {
  getProductHeadBioCertificateFilePath,
  getProductHeadBioCertificateRows,
  type ProductHeadBioCertificateLocation,
} from './productHeadBioCertificates'

const getBioCertificateLocationLabel = (
  location: ProductHeadBioCertificateLocation,
): JSX.Element | null => {
  switch (location) {
    case 'pilsen': {
      return <FormattedMessage id="general.label.czechia" />
    }
    case 'settala': {
      return <FormattedMessage id="footer.flag.italy.label" />
    }
    case 'sevenum': {
      return <FormattedMessage id="general.label.netherlands" />
    }
    default: {
      return null
    }
  }
}

export const ProductHeadBioLabel: FC = () => {
  const { locale, tenant } = useGlobalConfigContext()
  const { setMediaServerOrigin } = useCdnMediaServer()
  const dialogControls = useOpenableControls()
  const bioCertificateRows = getProductHeadBioCertificateRows({
    locale,
    tenant,
  })

  return (
    <>
      <ProductHeadGenericLabel
        className="border-dark-primary-min bg-light-primary-low"
        data-qa-id="pdp-bio-label"
        onClick={dialogControls.handleOpen}
      >
        <FormattedMessage id="general.label.bio" />
        <IconInfo aria-hidden className="ml-1 size-3.5" />
      </ProductHeadGenericLabel>

      <Dialog
        data-qa-id="pdp-bio-label-dialog"
        onClose={dialogControls.handleClose}
        open={dialogControls.isOpen}
      >
        <DialogTitle
          className="bg-light-primary-low"
          onClose={dialogControls.handleClose}
        >
          <FormattedMessage id="general.label.bio" />
        </DialogTitle>
        <DialogContent className="min-h-0 overflow-y-auto bg-light-primary-medium p-4 tablet:p-6">
          <div className="rounded-m bg-light-primary-low p-4 text-dark-primary-max">
            <p className="mb-3 text-s font-medium">
              <FormattedMessage id="general.label.bio.description" />
            </p>
            <p className="mb-3 text-s font-normal">
              <FormattedMessage id="general.label.bio.info" />
            </p>
            <div className="flex flex-col gap-3">
              {bioCertificateRows.map((row) => {
                const href = setMediaServerOrigin(
                  getProductHeadBioCertificateFilePath({
                    fileName: row.fileName,
                    tenant,
                  }),
                )

                return (
                  <a
                    className="flex min-h-14 items-center justify-between gap-3 rounded-m bg-light-brand-medium px-4 py-3 text-s text-dark-primary-max no-underline"
                    href={href}
                    key={row.location}
                  >
                    <span className="min-w-0 flex-1 break-words">
                      {getBioCertificateLocationLabel(row.location)}
                      {': '}
                      {row.certificateNumber}
                    </span>
                    <IconArrowRight
                      aria-hidden
                      className="size-5 shrink-0 text-dark-brand"
                    />
                  </a>
                )
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
